"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import { openAuthorizeUrl } from "@/utils/linkedaccounts/linkedAccounts";
import { MODAL_HEIGHT, MODAL_WIDTH, appConfig } from "@constants";
import type { QuestKey } from "@ui-kit/QuestList";
import { X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Tweet } from "react-tweet";

type LikeRetweetXModalProps = {
  visible: boolean;
  onClose: () => void;
  showConfirmationModal: () => void;
  tweetId: string;
  gameId: string;
  gameName: string;
  questRuleId: string;
  questKey: QuestKey;
  questLink: string;
};

export function LikeRetweetXModal({
  visible,
  onClose,
  questRuleId,
  questKey,
  questLink,
  showConfirmationModal,
  tweetId,
  gameId,
  gameName,
}: LikeRetweetXModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { trackV3 } = useAnalytics();

  const trackModalEvent = useCallback(
    (action: Action, extras: Record<string, unknown> = {}) => {
      trackV3({
        object: "Quest",
        action,
        extras: {
          ruleId: questRuleId,
          questKey: questKey,
          gameId: gameId,
          gameName: gameName,
          isLoggedIn: true,
          ...extras,
        },
      });
    },
    [trackV3, questRuleId, questKey, gameId, gameName],
  );

  useEffect(() => {
    if (visible) {
      trackModalEvent(Action.VIEWED, {
        section: "QuestModal",
        tweetId: tweetId,
      });
    }
  }, [visible, trackModalEvent, tweetId]);

  const handleClose = useCallback(() => {
    trackModalEvent(Action.PRESSED, {
      control: "Close",
      controlType: "Button",
      section: "QuestModal",
    });
    setError(null);
    onClose();
  }, [onClose, trackModalEvent]);

  const handleLikeAndRetweet = useCallback(async () => {
    if (!questRuleId || !questKey) return;

    trackModalEvent(Action.PRESSED, {
      control: "LikeAndRetweet",
      controlType: "Button",
      section: "QuestModal",
    });

    setError(null);
    setIsProcessing(true);

    try {
      localStorage.removeItem(questRuleId);

      showConfirmationModal();

      openAuthorizeUrl(
        questRuleId,
        questKey,
        questLink ?? "",
        gameName,
        gameId,
        false, // isPosting = false for like/retweet
        "", // No tweet content for like/retweet
      );

      trackModalEvent(Action.SUCCEEDED, {
        control: "LikeAndRetweet",
        section: "QuestModal",
      });

      handleClose();
    } catch (error) {
      console.error("Failed to initiate like and retweet:", error);
      setError("Failed to initiate like and retweet. Please try again.");

      trackModalEvent(Action.FAILED, {
        control: "LikeAndRetweet",
        section: "QuestModal",
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsProcessing(false);
    }
  }, [
    questRuleId,
    questKey,
    questLink,
    showConfirmationModal,
    gameName,
    gameId,
    handleClose,
    trackModalEvent,
  ]);

  if (questKey !== "x-like-and-retweet-validation") {
    return null;
  }

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-[9998] bg-black/50" />
        <DialogContent
          className="fixed left-1/2 top-1/2 z-[9999] flex flex-col items-center justify-between rounded-[20px] text-white shadow-xl -translate-x-1/2 -translate-y-1/2 p-0 border-0 [&>button]:hidden"
          style={{
            backgroundColor: "#131313",
            height: MODAL_HEIGHT,
            width: "calc(100dvw - 40px)",
            maxWidth: MODAL_WIDTH,
            backgroundImage: `url("${appConfig.ASSET_BASE_URL}/images/quests/follow-confirmation-background.svg")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top",
          }}
        >
          <DialogTitle className="sr-only">
            Like and Repost on X Quest Modal
          </DialogTitle>
          <DialogDescription className="sr-only">
            Modal for liking and reposting a tweet on X social media platform as
            part of a quest completion
          </DialogDescription>

          <div className="flex flex-col items-center w-full gap-4 pt-8">
            <h2
              className="text-center mx-12 text-white font-suisse"
              style={{
                fontSize: "28px",
                lineHeight: "32px",
                fontWeight: 700,
              }}
            >
              Like and Repost on X
            </h2>
          </div>

          <div className="flex-1 overflow-hidden px-4 w-full h-full overflow-y-auto">
            <div
              className="w-full max-w-[350px] mx-auto [&_.react-tweet-theme]:!bg-[#131313] [&_article]:!bg-[#131313] [&_.react-tweet]:!bg-[#131313] [&_*[data-theme]]:!bg-[#131313] [&_*]:!text-white [&_time]:!text-gray-400 [&_a]:!text-blue-400 [&_svg]:!fill-white [&_.react-tweet-header]:!text-white [&_.react-tweet-text]:!text-white [&_.react-tweet-text_a]:!text-blue-400"
              style={{
                colorScheme: "dark",
              }}
            >
              <Tweet id={tweetId} />
            </div>

            {error && (
              <p className="text-sm text-red-400 text-center max-w-[296px] mx-auto mt-4">
                {error}
              </p>
            )}
          </div>

          <div className="flex flex-col w-full px-4 pb-4">
            <Button
              variant="default"
              onClick={handleLikeAndRetweet}
              disabled={isProcessing}
              className="w-full h-12 my-2 font-roboto font-medium"
              size="lg"
            >
              {isProcessing ? "Processing..." : "Like and Repost"}
            </Button>
            <Button
              variant="ghost"
              onClick={handleClose}
              className="w-full h-12 font-roboto font-medium hover:bg-white/10 hover:text-white"
              size="lg"
            >
              Go back
            </Button>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-lg bg-transparent hover:bg-white/10 transition-colors opacity-70 hover:opacity-100"
            aria-label="Close modal"
          >
            <X className="h-6 w-6 text-white" />
          </button>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
